import type { InviteDto } from '@lagerfeuer/shared';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { Modal } from '../../components/Modal';
import { invitesApi } from '../../lib/endpoints';
import { paths } from '../../lib/routes';
import { useAppStore } from '../../store/app';
import { overlays } from '../../store/overlays';
import { addJoinedServer, inviteErrorText, parseInviteInput } from './inviteCode';
import { InvitePreview } from './InvitePreview';
import './servers.css';

/** Server über Link, Code oder Vanity-URL beitreten: erst Vorschau, dann Beitritt. */
export function JoinServerDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [invite, setInvite] = useState<InviteDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const alreadyMember = useAppStore((s) => (invite ? !!s.servers[invite.server.id] : false));

  const lookup = async (e: FormEvent) => {
    e.preventDefault();
    const code = parseInviteInput(input);
    if (!code) {
      setError('Bitte gib einen gültigen Einladungslink oder -code ein.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      setInvite(await invitesApi.get(code));
    } catch (err) {
      setInvite(null);
      setError(inviteErrorText(err));
    } finally {
      setBusy(false);
    }
  };

  const join = async () => {
    if (!invite) return;
    if (alreadyMember) {
      onClose();
      void navigate(paths.server(invite.server.id));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const full = await invitesApi.accept(invite.code);
      addJoinedServer(full);
      onClose();
      void navigate(paths.server(full.id));
    } catch (err) {
      setError(inviteErrorText(err));
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Einem Server beitreten"
      onClose={onClose}
      testId="join-server-dialog"
      footer={
        <>
          <button type="button" className="btn btn-link" onClick={() => overlays.open({ type: 'createServer' })} data-testid="join-server-to-create">
            Eigenen Server erstellen
          </button>
          <span className="spacer" />
          {invite ? (
            <>
              <button type="button" className="btn btn-ghost" onClick={() => setInvite(null)} data-testid="join-server-back">
                Zurück
              </button>
              <button type="button" className="btn btn-primary" onClick={() => void join()} disabled={busy} data-testid="join-server-submit">
                {alreadyMember ? 'Server öffnen' : busy ? 'Beitreten …' : 'Beitreten'}
              </button>
            </>
          ) : (
            <button type="submit" form="join-server-form" className="btn btn-primary" disabled={busy || !input.trim()} data-testid="join-server-lookup">
              {busy ? 'Suchen …' : 'Einladung prüfen'}
            </button>
          )}
        </>
      }
    >
      {invite ? (
        <div className="stack">
          <InvitePreview invite={invite} />
          {alreadyMember && <p className="muted small">Du bist bereits Mitglied dieses Servers.</p>}
        </div>
      ) : (
        <form id="join-server-form" className="stack" onSubmit={lookup} noValidate>
          <p className="muted" style={{ margin: 0 }}>
            Füge einen Einladungslink, einen Code oder eine Vanity-URL ein.
          </p>
          <div className="field">
            <label htmlFor="join-server-input">Einladungslink</label>
            <input
              id="join-server-input"
              className="input"
              placeholder={`${window.location.origin}/invite/abcd1234`}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              aria-invalid={!!error}
              autoFocus
              data-testid="join-server-input"
            />
            <span className="field-hint">Beispiele: abcd1234 · lagerfeuer-freunde · {window.location.host}/invite/abcd1234</span>
          </div>
        </form>
      )}
      {error && (
        <p className="error-text" role="alert" data-testid="join-server-error">
          {error}
        </p>
      )}
    </Modal>
  );
}
